import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Button, Space, Tag, Select, Typography, Alert, Row, Col } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EyeOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useCustomerCreditNotes, usePostCustomerCreditNote, useCancelCustomerCreditNote } from '@/hooks/useCustomerCreditNote';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { CustomerCreditNote } from '@/services/customerCreditNote.service';

const { Title, Text } = Typography;

const CustomerCreditNotesPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [statusFilter, setStatusFilter] = useState<string>('');

  const filters = { status: statusFilter || undefined };
  const { data: list = [], isLoading } = useCustomerCreditNotes(filters);
  const postMutation = usePostCustomerCreditNote();
  const cancelMutation = useCancelCustomerCreditNote();

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[s] || s;
  };

  const columns: ColumnsType<CustomerCreditNote> = [
    {
      title: t('creditNoteNumber'),
      dataIndex: 'credit_note_number',
      key: 'credit_note_number',
      fixed: 'left',
      width: 140,
      render: (val: string, record: CustomerCreditNote) => (
        <a onClick={() => navigate(`/fin/credit-notes/${record.id}`)}>{val ?? '—'}</a>
      ),
    },
    { title: t('date'), dataIndex: 'credit_note_date', key: 'credit_note_date', width: 110, render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-') },
    { title: t('customer'), key: 'customer', width: 200, render: (_, r) => (r as any).customer?.customer_name ?? r.customer_id ?? '-' },
    { title: t('amount'), dataIndex: 'total_amount', key: 'total_amount', width: 110, align: 'right', render: (v: number) => formatCurrency(Number(v)) },
    { title: t('reason'), dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => {
        const color = s === 'Posted' ? 'green' : s === 'Cancelled' ? 'red' : 'default';
        return <Tag color={color}>{statusLabel(s)}</Tag>;
      },
    },
    {
      title: tc('actions'),
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/fin/credit-notes/${record.id}`)}>
            {tc('view')}
          </Button>
          {record.status === 'Draft' && (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined />}
              loading={postMutation.isPending}
              onClick={() => postMutation.mutate(record.id)}
            >
              {tc('post')}
            </Button>
          )}
          {record.status === 'Posted' && (
            <Button
              type="link"
              size="small"
              danger
              icon={<StopOutlined />}
              loading={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate(record.id)}
            >
              {tc('cancel')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="top" gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>{t('customerCreditNotes')}</Title>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>{t('arCreditNotesHint')}</Text>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16, maxWidth: 720 }}
            message={t('creditNoteRegister')}
            description={t('creditNoteRegisterDesc')}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/credit-notes/new')}>
            {t('newCreditNote')}
          </Button>
        </Col>
      </Row>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder={t('status')}
            allowClear
            style={{ width: 120 }}
            value={statusFilter || undefined}
            onChange={setStatusFilter}
            options={[
              { value: '', label: t('all') },
              { value: 'Draft', label: t('statusDraft') },
              { value: 'Posted', label: t('statusPosted') },
              { value: 'Cancelled', label: t('statusCancelled') },
            ]}
          />
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={isLoading}
          sticky={{ offsetHeader: 0 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('totalCreditNotes', { count: total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1100, y: 520 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default CustomerCreditNotesPage;
